/**
 * Kanban Column Component
 * 
 * Renders a single column in the Kanban board with virtualized item rendering.
 * Uses react-window FixedSizeList for efficient rendering of large item lists.
 * 
 * Features:
 * - Virtualized scrolling for performance with many items
 * - Only renders visible items plus overscan buffer
 * - Chevron-styled column headers
 * - Drag and drop support
 * - Graceful fallback for empty columns
 * 
 * Performance Notes:
 * - ITEM_HEIGHT set to 200px (180px card + 16px gap)
 * - Overscan count of 3 items provides smooth scrolling
 * - Column height calculated from parent container
 */

import { FC, useRef, useEffect, useState } from 'react';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import KanbanCard from './KanbanCard';

// Card height (min-h-[180px]) + gap spacing (16px)
const ITEM_HEIGHT = 196;
const OVERSCAN_COUNT = 3;

interface KanbanItem {
  id: string;
  title: string;
  subtitle?: string;
  data: any;
  columnId: string;
}

interface KanbanColumn {
  id: string;
  title: string;
  items: KanbanItem[];
}

interface KanbanColumnProps {
  column: KanbanColumn;
  entityType: number;
  searchQuery?: string;
  isFirst?: boolean;
  isLast?: boolean;
  onDragStart: (id: string) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, columnId: string) => void;
  onDragLeave: () => void;
  onDrop: (columnId: string) => Promise<void>;
  draggingId: string | null;
  hoveredColumnId: string | null;
}

// Row renderer for virtualized list
interface RowData {
  items: KanbanItem[];
  entityType: number;
  searchQuery: string;
  onDragStart: (id: string) => void;
  draggingId: string | null;
}

const Row: FC<ListChildComponentProps<RowData>> = ({ index, style, data }) => {
  const { items, entityType, searchQuery, onDragStart, draggingId } = data;
  const item = items[index];

  return (
    <div style={{ ...style, paddingBottom: 16 }}>
      <KanbanCard
        item={item}
        entityType={entityType}
        searchQuery={searchQuery}
        onDragStart={onDragStart}
        draggingId={draggingId}
      />
    </div>
  );
};

const KanbanColumn: FC<KanbanColumnProps> = ({
  column,
  entityType,
  searchQuery = '',
  isFirst = false,
  isLast = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  draggingId,
  hoveredColumnId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(500);
  const isHovered = hoveredColumnId === column.id;

  // Measure container height for virtualized list
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        // Subtract padding (16px top + 16px bottom)
        const height = containerRef.current.clientHeight - 32;
        setContainerHeight(Math.max(height, 200));
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Get the appropriate clip-path based on position
  const getClipPath = () => {
    if (isFirst) {
      // First column: straight left edge, arrow right edge
      return 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)';
    } else if (isLast) {
      // Last column: arrow left edge, straight right edge  
      return 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)';
    } else {
      // Middle columns: arrow both sides
      return 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)';
    }
  };

  // Prepare item data for virtualized rendering
  const itemData: RowData = {
    items: column.items,
    entityType,
    searchQuery,
    onDragStart,
    draggingId
  };

  return (
    <div className="flex flex-col min-w-[300px] max-w-[300px] h-full">
      {/* Chevron-styled Column Header */}
      <div className="relative flex-shrink-0">
        <div className="bg-green-200 text-green-800 px-6 py-3 font-semibold text-sm relative"
          style={{
            clipPath: getClipPath()
          }}>
          <div className="flex items-center justify-between">
            <span>{column.title}</span>
            <span className="ml-2 font-bold">{column.items.length}</span>
          </div>
        </div>
      </div>

      {/* Virtualized Column Body */}
      <div
        ref={containerRef}
        onDragOver={(e) => onDragOver(e, column.id)}
        onDragLeave={onDragLeave}
        onDrop={() => onDrop(column.id)}
        className={`flex-1 p-4 bg-gray-50 dark:bg-gray-900 min-h-[500px]
          ${isHovered ? 'bg-green-50 dark:bg-green-900/10 border-2 border-dashed border-green-500' : 'border border-gray-200'}
          transition-all duration-200`}
      >
        {column.items.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Drop items here
            </p>
          </div>
        ) : (
          <List
            height={containerHeight}
            itemCount={column.items.length}
            itemSize={ITEM_HEIGHT}
            width="100%"
            itemData={itemData}
            overscanCount={OVERSCAN_COUNT}
          >
            {Row}
          </List>
        )}
      </div>
    </div>
  );
};

export default KanbanColumn;